/**
 * Expected-completion helpers for background jobs.
 *
 * Every job table (llm_jobs, qc_jobs, shot_video_request, video_upscale_jobs,
 * render_jobs) carries `expected_completion_at`: the completion promised at
 * submission, a timestamptz, so an absolute instant. NULL means nothing was
 * promised — a normal state, never a missing value. Render it with
 * `<app-job-eta>`; these helpers are for screens that need the raw numbers.
 */

/** A real promise is minutes away; anything further out is the skew below. */
const ETA_MAX_AHEAD_MS = 2 * 60 * 60 * 1000;
/** Video rows stamped before the backend anchor fix (5 Oct) are 5.5 h late. */
const IST_SKEW_MS = 5.5 * 60 * 60 * 1000;

const IST_TIME = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/**
 * Epoch ms of a promised completion, or null when absent or untrustworthy.
 *
 * ⚠ Some shot_video_request rows were stamped by a cron that read an IST start
 *   as UTC, so they are exactly 5.5 h late. A fixed stamp is never more than a
 *   couple of hours out, so shifting only those can't double-correct.
 */
export function parseJobEta(
  value: string | number | Date | null | undefined,
  now = Date.now(),
): number | null {
  if (value == null || value === "") return null;
  let ms = value instanceof Date ? value.getTime() : typeof value === "number" ? value : Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  if (ms - now > ETA_MAX_AHEAD_MS) ms -= IST_SKEW_MS;
  if (ms - now > ETA_MAX_AHEAD_MS) return null;
  return ms;
}

/** "About 4 min left" → "30s left" → "Taking longer than usual…". */
export function jobEtaCountdown(etaMs: number, now = Date.now()): string {
  const left = Math.ceil((etaMs - now) / 1000);
  if (left <= 0) return "Taking longer than usual…";
  if (left <= 90) return `${left}s left`;
  return `About ${Math.ceil(left / 60)} min left`;
}

/** The promised time on the IST clock, e.g. "11:22 AM IST". */
export function jobEtaClock(etaMs: number): string {
  return `${IST_TIME.format(etaMs).toUpperCase()} IST`;
}

/**
 * The latest promise among several running jobs — what a single overlay over a
 * batch should count down to. Null when none of them carries one.
 */
export function latestJobEta(
  values: Array<string | number | Date | null | undefined>,
  now = Date.now(),
): number | null {
  let latest: number | null = null;
  for (const v of values) {
    const ms = parseJobEta(v, now);
    if (ms != null && (latest == null || ms > latest)) latest = ms;
  }
  return latest;
}
